// ── Core data types ──────────────────────────────────────

export interface CanvasNode {
  id: string
  kind: string
  name: string
  spec: Record<string, unknown>
  x: number
  y: number
  width: number
  height: number
  color: string
  parentId?: string
}

export interface CanvasEdge {
  fromId: string
  toId: string
  type: string
  edgeKind: string
  color: string
  dash?: number[]
}

// ── Viewport / camera ────────────────────────────────────

export interface ViewportRect {
  x: number
  y: number
  width: number
  height: number
}

export interface ViewportInsets {
  top?: number
  right?: number
  bottom?: number
  left?: number
}

export interface CameraState {
  x: number
  y: number
  scale: number
}

// ── LOD ──────────────────────────────────────────────────

export enum LODLevel {
  Dot = 0,
}

// ── Config ───────────────────────────────────────────────

export type LayoutDirection = "DOWN" | "RIGHT"
export type LayoutAlgorithm =
  | "layered"
  | "mrtree"
  | "stress"
  | "force"
  | "radial"

export const LAYOUT_ALGORITHMS: {
  id: LayoutAlgorithm
  label: string
  desc: string
}[] = [
  {
    id: "layered",
    label: "Layered",
    desc: "Hierarchical Sugiyama — slow on large graphs",
  },
  { id: "mrtree", label: "Tree", desc: "Fast tree layout" },
  {
    id: "stress",
    label: "Stress",
    desc: "Stress minimization — good for clusters",
  },
  { id: "force", label: "Force", desc: "Force-directed — fast, organic" },
  { id: "radial", label: "Radial", desc: "Radial tree layout" },
]

export interface GraphCanvasConfig {
  background: string
  gridColor: string
  gridSize: number
  edgeWidth: number
  selectionColor: string
  labelBackground: string
  labelColor: string
  dimAlpha: number
  layoutDirection: LayoutDirection
  showLabels: boolean
}

export const DEFAULT_CONFIG: GraphCanvasConfig = {
  background: "#0a0a0a",
  gridColor: "#1a1a1a",
  gridSize: 20,
  edgeWidth: 1.5,
  selectionColor: "#fafafa",
  labelBackground: "rgba(10, 10, 10, 0.7)",
  labelColor: "#fafafa",
  dimAlpha: 0.08,
  layoutDirection: "DOWN",
  showLabels: true,
}

// ── Edge batching ───────────────────────────────────────

export interface RenderEdge {
  from: number
  to: number
}

export interface EdgeBatch {
  edgeKind: string
  color: string
  dash: number[]
  edges: RenderEdge[]
}

// ── Render scene (passed to IRenderer) ───────────────────

export interface RenderTimings {
  clear: number
  grid: number
  edges: number
  nodes: number
  labels: number
}

export interface DebugStats {
  fps: number
  renderFps: number
  rafMs: number
  renderMs: number
  renderAvgMs: number
  cacheHit: boolean
  edgeCount: number
  nodeCount: number
  visibleNodes: number
  lod: string
  scale: number
  animating: boolean
  dirty: boolean
  timings: RenderTimings
}

export interface RenderScene {
  visibleIndices: number[]
  edgeBatches: EdgeBatch[]
  camera: CameraState
  lod: LODLevel
  config: GraphCanvasConfig
  selectedId: string | null
  hoveredId: string | null
  highlightedIds: Set<string> | null
  attentionIds: Set<string> | null
  timeSec: number
  canvasWidth: number
  canvasHeight: number
  animating: boolean
  interacting: boolean
}

// ── Callbacks ────────────────────────────────────────────

export interface GraphCanvasCallbacks {
  onNodeClick?: (nodeId: string) => void
  onNodeHover?: (nodeId: string | null) => void
  onPaneClick?: () => void
  onRender?: () => void
}
